/**
 * useADLSignature Hook
 * CCH Care — Phase 3 → Phase 5
 *
 * Phase 5 fixes:
 * - Modal now closes when section is already signed (409/422) — treats it as success
 * - Signing state is set immediately on confirm click before the API call
 * - HTML entity decoding handled upstream in signerName prop
 *
 * Phase 5e addition:
 * - signSilently(section) — signs a section without opening the modal.
 *   Used by attestation flow so attested sections immediately show "Signed"
 *   without requiring a second user interaction.
 *
 * Late Documentation Phase 3:
 * - confirmAmendment now surfaces a {success:false} response as an error
 *   instead of silently no-opping (previously the modal stayed open with
 *   no feedback on a non-thrown failure).
 */
import { useState, useCallback } from 'react';
import { signSection, supervisorSignRecord, submitAmendment } from '../api/adl';

export default function useADLSignature(adlRecordId, { onSectionSigned, onRecordReviewed, onAmendmentSaved } = {}) {
  const [pendingSection,      setPendingSection]      = useState(null);
  const [supervisorModalOpen, setSupervisorModalOpen] = useState(false);
  const [pendingAmendment,    setPendingAmendment]    = useState(null);
  const [signing,             setSigning]             = useState(false);
  const [signError,           setSignError]           = useState(null);
  const [amending,            setAmending]            = useState(false);
  const [amendError,          setAmendError]          = useState(null);

  // ── CNA: initiate section sign-off ───────────────────────
  const initiateSign = useCallback((section) => {
    setSignError(null);
    setPendingSection(section);
  }, []);

  const cancelSign = useCallback(() => {
    setPendingSection(null);
    setSignError(null);
  }, []);

  const confirmSign = useCallback(async (notes = null) => {
    if (!pendingSection || !adlRecordId) return;
    setSigning(true);
    setSignError(null);
    try {
      const data = await signSection(adlRecordId, pendingSection, notes);
      if (data.success) {
        const sectionKey = pendingSection;
        setPendingSection(null);
        onSectionSigned?.(sectionKey, {
          section:         data.section,
          signed_at:       data.signed_at,
          sections_signed: data.sections_signed,
          all_signed:      data.all_signed,
        });
      } else {
        setSignError(data.error || 'Failed to sign section');
      }
    } catch (err) {
      const msg    = err?.response?.data?.error || err.message || 'Failed to sign section';
      const status = err?.response?.status;

      // 409 = already signed — treat as success and close modal
      if (status === 409 || msg.includes('already been signed')) {
        const sectionKey = pendingSection;
        setPendingSection(null);
        onSectionSigned?.(sectionKey, {
          section:   sectionKey,
          signed_at: new Date().toISOString(),
        });
        return;
      }

      setSignError(msg);
    } finally {
      setSigning(false);
    }
  }, [adlRecordId, pendingSection, onSectionSigned]);

  // ── Silent sign — no modal, used by attestation flow ─────
  // Called immediately after "Confirm — Nothing to Report" so the
  // section badge flips to "Signed" without a second user interaction.
  const signSilently = useCallback(async (section) => {
    if (!adlRecordId || !section) return;
    try {
      const data = await signSection(adlRecordId, section, 'Attested: nothing to report');
      if (data.success) {
        onSectionSigned?.(section, {
          section,
          signed_at:       data.signed_at,
          sections_signed: data.sections_signed,
          all_signed:      data.all_signed,
        });
      }
    } catch (err) {
      const status = err?.response?.status;
      const msg    = err?.response?.data?.error || err.message || '';

      // 409 = already signed — still notify so UI stays in sync
      if (status === 409 || msg.includes('already been signed')) {
        onSectionSigned?.(section, {
          section,
          signed_at: new Date().toISOString(),
        });
        return;
      }

      // Silent — log but don't surface an error modal
      console.error('signSilently failed for', section, ':', msg);
    }
  }, [adlRecordId, onSectionSigned]);

  // ── Amendment ────────────────────────────────────────────
  const initiateAmendment = useCallback((section) => {
    setAmendError(null);
    setPendingAmendment({ section });
  }, []);

  const cancelAmendment = useCallback(() => {
    setPendingAmendment(null);
    setAmendError(null);
  }, []);

  const confirmAmendment = useCallback(async ({ section, sectionRowId, fieldName, oldValue, newValue, reason }) => {
    if (!adlRecordId) return;
    try {
      setAmending(true);
      setAmendError(null);
      const data = await submitAmendment(adlRecordId, {
        section, sectionRowId, fieldName, oldValue, newValue, reason,
      });
      if (data.success) {
        setPendingAmendment(null);
        onAmendmentSaved?.(section, data);
      } else {
        setAmendError(data.error || 'Failed to submit amendment');
      }
    } catch (err) {
      setAmendError(err?.response?.data?.error || err.message || 'Failed to submit amendment');
    } finally {
      setAmending(false);
    }
  }, [adlRecordId, onAmendmentSaved]);

  // ── Supervisor co-sign ────────────────────────────────────
  const initiateSupervisorSign = useCallback(() => {
    setSignError(null);
    setSupervisorModalOpen(true);
  }, []);

  const cancelSupervisorSign = useCallback(() => {
    setSupervisorModalOpen(false);
    setSignError(null);
  }, []);

  const confirmSupervisorSign = useCallback(async (notes = null) => {
    if (!adlRecordId) return;
    try {
      setSigning(true);
      setSignError(null);
      const data = await supervisorSignRecord(adlRecordId, notes);
      if (data.success) {
        setSupervisorModalOpen(false);
        onRecordReviewed?.(data);
      }
    } catch (err) {
      setSignError(err?.response?.data?.error || err.message || 'Failed to submit co-sign');
    } finally {
      setSigning(false);
    }
  }, [adlRecordId, onRecordReviewed]);

  return {
    // Modal sign flow
    pendingSection,
    initiateSign,
    cancelSign,
    confirmSign,
    isModalOpen: !!pendingSection,

    // Silent sign (attestation flow)
    signSilently,

    // Amendment flow
    pendingAmendment,
    initiateAmendment,
    cancelAmendment,
    confirmAmendment,
    amending,
    amendError,
    isAmendmentModalOpen: !!pendingAmendment,

    // Supervisor co-sign flow
    supervisorModalOpen,
    initiateSupervisorSign,
    cancelSupervisorSign,
    confirmSupervisorSign,

    signing,
    signError,
  };
}